<script setup>
/* Ausklappmenue in der Gestaltung der App.
 *
 * Ersetzt v-menu/v-list aus Vuetify: Deren Vorgabegestaltung (heller
 * Materialkasten, eigene Schrift, eigene Abstaende) fiel in der dunkelgruenen
 * Oberflaeche als Fremdkoerper auf. Aussehen und Verhalten richten sich hier
 * nach dem Benachrichtigungsfeld, das es in der urspruenglichen Fassung schon
 * gab — siehe .notif-panel in styles/main.css.
 *
 * Verwendung:
 *   <DropdownMenu align="right" :width="240">
 *     <template #trigger="{ toggle, open }">…</template>
 *     <template #default="{ close }">
 *       <button class="dd-item" @click="close">…</button>
 *     </template>
 *   </DropdownMenu>
 *
 * Die Klassen dd-head, dd-item, dd-sep und dd-badge stehen unten bereit und
 * gelten auch fuer eingesetzten Inhalt.
 */

import { ref, onMounted, onBeforeUnmount } from 'vue';

defineProps({
  /** An welcher Kante haengt das Feld? */
  align: { type: String, default: 'right' },
  width: { type: Number, default: 220 },
});

const open = ref(false);
const root = ref(null);

function toggle() {
  open.value = !open.value;
}
function close() {
  open.value = false;
}

function onDocumentClick(e) {
  if (open.value && root.value && !root.value.contains(e.target)) close();
}
function onKey(e) {
  if (e.key === 'Escape') close();
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick);
  document.addEventListener('keydown', onKey);
});
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick);
  document.removeEventListener('keydown', onKey);
});

defineExpose({ close });
</script>

<template>
  <div ref="root" class="dd-root">
    <slot name="trigger" :toggle="toggle" :open="open" :close="close" />

    <transition name="dd">
      <div
        v-if="open"
        class="dd-panel"
        :class="align === 'left' ? 'left' : 'right'"
        :style="{ width: width + 'px' }"
        role="menu"
      >
        <slot :close="close" />
      </div>
    </transition>
  </div>
</template>

<style scoped>
.dd-root {
  position: relative;
  display: inline-flex;
}

.dd-panel {
  position: absolute;
  top: calc(100% + 6px);
  z-index: 12;
  max-width: 88vw;
  max-height: 70vh;
  overflow-y: auto;
  padding: 5px;
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: 0 12px 40px var(--shadow-strong);
  text-align: left;
}
.dd-panel.right {
  right: 0;
}
.dd-panel.left {
  left: 0;
}

/* Einblenden — kurz und knapp, das Menue soll nicht schweben. */
.dd-enter-active,
.dd-leave-active {
  transition: opacity 0.13s ease, transform 0.13s ease;
}
.dd-enter-from,
.dd-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Die folgenden Klassen gelten auch fuer eingesetzten Inhalt — deshalb
   :deep(). Ohne das erreicht die Gestaltung den Inhalt der Slots nicht. */

/* Kopfzeile: Name, Rolle, alles Nicht-Anklickbare */
.dd-panel :deep(.dd-head) {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 11px 11px;
}
.dd-panel :deep(.dd-head-text) {
  min-width: 0;
}
.dd-panel :deep(.dd-name) {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dd-panel :deep(.dd-sub) {
  font-size: 0.7rem;
  color: var(--dim);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Anklickbarer Eintrag */
.dd-panel :deep(.dd-item) {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 11px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--text);
  font-family: var(--sans);
  font-size: 0.79rem;
  text-align: left;
  cursor: pointer;
  transition: background 0.14s, color 0.14s;
}
.dd-panel :deep(.dd-item:hover) {
  background: var(--card-hover);
}
.dd-panel :deep(.dd-item svg) {
  flex: none;
  color: var(--muted);
}
.dd-panel :deep(.dd-item:hover svg) {
  color: var(--green);
}
.dd-panel :deep(.dd-item.active) {
  color: var(--green);
}
.dd-panel :deep(.dd-item.active svg) {
  color: var(--green);
}
.dd-panel :deep(.dd-item .dd-label) {
  flex: 1;
  min-width: 0;
}

/* Zahl am rechten Rand eines Eintrags — dieselbe Form wie an der Glocke */
.dd-panel :deep(.dd-badge) {
  flex: none;
  min-width: 16px;
  padding: 1px 6px;
  border-radius: 10px;
  background: var(--green);
  color: rgb(var(--v-theme-on-primary));
  font-size: 0.64rem;
  font-weight: 700;
  line-height: 1.4;
  text-align: center;
}

.dd-panel :deep(.dd-sep) {
  height: 1px;
  margin: 5px 4px;
  background: var(--border-soft);
}
</style>
